import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import FormInput from '../../../components/form/FormInput';
import FormToggle from '../../../components/form/FormToggle';
import { allModulePermissionGet } from '../../../store/slices/adminSlices/modulePermissionSlice';
import {
  companyRoleAdd,
  companyRoleGetById,
  companyRoleUpdate
} from '../../../store/slices/adminSlices/companyRoleSlice';

const AddEditCompanyRoleModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const [modulePermissions, setModulePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const initialValues = {
    name: '',
    status: 'true',
    role_permission: []
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required')
  });

  const onSubmit = async () => {
    const payload = { ...values, role_permission: selectedPermissions };

    if (isEdit) {
      try {
        payload['id'] = editRecord?.id;
        const response = await dispatch(companyRoleUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const response = await dispatch(companyRoleAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchAllModulePermission = async () => {
    try {
      const response = await dispatch(allModulePermissionGet()).unwrap();
      if (response && response?.success) {
        const filteredModules = response?.data?.filter((module) => {
          const allPermissionsFalse = Object.values(module.permissions).every((value) => !value);
          return !allPermissionsFalse;
        });

        setModulePermissions(filteredModules || []);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchCompanyRoleById = async () => {
    try {
      const response = await dispatch(companyRoleGetById(editRecord?.id)).unwrap();
      if (response && response?.success) {
        setValues({
          name: response?.companyRole?.name,
          status: String(response?.companyRole?.status)
        });

        // Update selectedPermissions for edit mode
        const rolePermissions = [...(response?.rolePermission || [])]?.map((val) => ({
          module_permission_id: val?.module_permission_id,
          permission: val?.permission
        }));

        setSelectedPermissions(rolePermissions || []);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handlePermissionChange = (moduleId, permissionKey, checked) => {
    const updatedPermissions = [...selectedPermissions];

    // For new roles, all permissions should be true when selected
    if (!isEdit) {
      const existingModule = updatedPermissions.find(
        (item) => item.module_permission_id === moduleId
      );

      if (existingModule) {
        existingModule.permission[permissionKey] = checked;
      } else {
        updatedPermissions.push({
          module_permission_id: moduleId,
          permission: { [permissionKey]: checked }
        });
      }
    } else {
      // For edit mode, only update the permission for that specific key
      const existingModule = updatedPermissions.find(
        (item) => item.module_permission_id === moduleId
      );

      if (existingModule) {
        if (checked) {
          existingModule.permission[permissionKey] = true;
        } else {
          delete existingModule.permission[permissionKey];
        }
      } else if (checked) {
        updatedPermissions.push({
          module_permission_id: moduleId,
          permission: { [permissionKey]: checked }
        });
      }
    }

    setSelectedPermissions(updatedPermissions);
  };

  useEffect(() => {
    if (isOpen) {
      fetchAllModulePermission();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isEdit) {
      const allPermissions = modulePermissions.map((module) => ({
        module_permission_id: module.id,
        permission: Object.keys(module.permissions).reduce((acc, permission) => {
          acc[permission] = module.permissions[permission];
          return acc;
        }, {})
      }));
      setSelectedPermissions(allPermissions);
    }
  }, [isEdit, modulePermissions]);

  useEffect(() => {
    if (isEdit && editRecord) {
      fetchCompanyRoleById();
    }
  }, [editRecord, isEdit]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Company Role' : 'Add Company Role'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="6" md="12">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="6" md="12">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
          </Row>

          <div className="w-100 py-3 px-4 mb-3 bg-primary text-white fw-bold rounded">
            Role Permissions
          </div>

          <div className="module-permissions">
            {modulePermissions?.map((module) => (
              <Row key={module.id} className="module-card mb-3 p-3 border rounded mx-1">
                <Col lg="4" className="module-name">
                  <strong>{module.module_name}</strong>
                </Col>
                <Col lg="8">
                  <Row>
                    {Object.keys(module.permissions)
                      .filter((permission) => module.permissions[permission]) // Only show active permissions
                      .map((permissionKey) => (
                        <Col key={permissionKey} lg="2" sm="6">
                          <div className="d-flex flex-row">
                            <div className="me-1">
                              <FormGroup check>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    id="check_header"
                                    checked={selectedPermissions.some(
                                      (perm) =>
                                        perm.module_permission_id === module.id &&
                                        perm.permission[permissionKey]
                                    )}
                                    onChange={(e) =>
                                      handlePermissionChange(
                                        module.id,
                                        permissionKey,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </Label>
                              </FormGroup>
                            </div>
                            <div className="text-primary fw-bold">
                              {permissionKey.replace('is_', '').replace('_', ' ').toUpperCase()}
                            </div>
                          </div>
                        </Col>
                      ))}
                  </Row>
                </Col>
              </Row>
            ))}
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditCompanyRoleModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditCompanyRoleModal;
